import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import T from '../i18n/content.js';
import { useLang, useT } from '../i18n/LangContext.jsx';
import { publicInsights } from '../api/client.js';

function Mark() {
  const { lang } = useLang();
  const navigate = useNavigate();
  return (
    <button className="mark--compact" onClick={() => navigate('/')} aria-label="Dexon — home">
      {lang === 'en' ? (
        <span>DEXON</span>
      ) : (
        <span className="cn">德 铉</span>
      )}
    </button>
  );
}

const NAV_ITEMS = [
  { key: 'firm',     path: '/firm' },
  { key: 'practice', path: '/practice' },
  { key: 'founder',  path: '/founder' },
  { key: 'insights', path: '/insights' }
];

export default function Topbar() {
  const { lang, setLang } = useLang();
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Warm the first page while visitors read another public page. Navigation
  // shares this request, so clicking early does not start a duplicate fetch.
  useEffect(() => {
    if (pathname === '/insights' || navigator.connection?.saveData) return;
    const prefetch = () => { publicInsights.prefetch(lang); };
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(prefetch, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 300);
    return () => clearTimeout(id);
  }, [lang, pathname]);

  const prefetchLink = path => {
    if (path === '/insights') publicInsights.prefetch(lang);
  };

  // Close the mobile menu on any route change (covers the logo, nav links and
  // browser back/forward).
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  // Esc closes the open menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <header className="topbar">
      <div className="topbar__inner">
        <Mark />
        <nav id="primary-nav" className={`nav ${menuOpen ? 'is-open' : ''}`}>
          {NAV_ITEMS.map(({ key, path }) => (
            <button
              key={key}
              className={pathname === path ? 'is-active' : ''}
              onPointerEnter={() => prefetchLink(path)}
              onFocus={() => prefetchLink(path)}
              onTouchStart={() => prefetchLink(path)}
              onClick={() => { prefetchLink(path); navigate(path); setMenuOpen(false); }}
            >
              {t(T.nav[key])}
            </button>
          ))}
        </nav>
        <div className="lang" role="group" aria-label="Language">
          <button className={lang === 'en' ? 'is-on' : ''} onClick={() => { setLang('en'); setMenuOpen(false); }}>EN</button>
          <span className="sep">/</span>
          <button className={lang === 'cn' ? 'is-on' : ''} onClick={() => { setLang('cn'); setMenuOpen(false); }}>中</button>
        </div>
        {/* Mobile-only (≤980px) menu toggle; hidden on desktop via CSS. */}
        <button
          className="nav-toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="primary-nav"
          onClick={() => setMenuOpen(o => !o)}
        >
          <span className="nav-toggle__bar" />
          <span className="nav-toggle__bar" />
          <span className="nav-toggle__bar" />
        </button>
      </div>
    </header>
  );
}
